import Edit from "@mui/icons-material/Edit";
import { Box, GridLegacy as Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { tabsOffset } from "components/TabLinks";
import { Issue } from "generated-graphql/graphql";
import { useMemo, useState } from "react";

import { useFacility, useStateInformationIssues } from "../useFacility";
import { EditStateInformationDialog } from "./EditStateInformationDialog";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const STATE_INFORMATION_SECTION_ID = "state-information";
export const STATE_INFORMATION_SECTION_ICON = "🏛️";

/** Group issues by their key for per-field display */
function useIssuesByKey(issues: Issue[]) {
  return useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const issue of issues) {
      if (issue.key) {
        const existing = map.get(issue.key) ?? [];
        existing.push(issue);
        map.set(issue.key, existing);
      }
    }
    return map;
  }, [issues]);
}

export const StateInformationSection: React.FC = () => {
  const theme = useTheme();
  const { data } = useFacility();
  const facility = data?.facility;
  const isCA = facility?.state === "CA";
  const stateFields = facility?.stateFields ?? [];
  const [editOpen, setEditOpen] = useState(false);

  const issues = useStateInformationIssues();
  const issuesByKey = useIssuesByKey(issues);

  return (
    <Box
      id={STATE_INFORMATION_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={5}>
        <Typography variant="h6">
          {STATE_INFORMATION_SECTION_ICON} State Information (Chemicals)
        </Typography>
        {!isCA && stateFields.length > 0 && (
          <IconButton
            size="small"
            className="edit-icon"
            sx={{ color: "action.selected", mr: 2 }}
            onClick={() => setEditOpen(true)}
          >
            <Edit />
          </IconButton>
        )}
      </Stack>

      {editOpen && <EditStateInformationDialog onClose={() => setEditOpen(false)} />}

      {isCA ? (
        <Typography variant="body2" color="text.secondary">
          California HMBP questions appear in the facility's Tier II report in Chemicals.
        </Typography>
      ) : stateFields.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No additional state information needed.
        </Typography>
      ) : (
        <Grid container columnSpacing={3} rowSpacing={2}>
          {stateFields.map((field) => (
            <Grid item xs={12} md={6} key={field.key}>
              <DisplayOnlyField
                label={<span style={{ whiteSpace: "normal" }}>{field.label ?? field.key}</span>}
                text={field.value ?? ""}
                issues={issuesByKey.get(field.key)}
              />
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};
